export const UNIT_MEASURE_CODE = 1;
export const DEFAULT_QUANTITY_DECIMALS = 0;
export const MAX_QUANTITY_DECIMALS = 2;
export const QUANTITY_BUTTON_STEP = 1;

export interface ProductQuantityRules {
    cantDecimal?: number | null;
    storefrontMinimum?: number | null;
    stock?: number | null;
}

export interface ProductQuantityStockRules {
    canonicalStock?: number | null;
    legacyStock?: number | null;
    unitMeasureCode?: number | null;
    allowWithoutStock?: boolean | null;
}

export interface ProductQuantityStockResolution {
    stock: number | null;
    missingCanonicalStock: boolean;
    usedLegacyStock: boolean;
}

export type ProductQuantityValidationError =
    | "not-finite"
    | "not-positive"
    | "invalid-precision"
    | "below-minimum"
    | "above-stock";

export interface ProductQuantityValidationResult {
    valid: boolean;
    error?: ProductQuantityValidationError;
}

const getFactor = (precision: number): number => 10 ** resolvePrecision(precision);

const hasValidPrecision = (value: number, precision: number): boolean => {
    const scaledValue = value * getFactor(precision);
    const tolerance = Number.EPSILON * Math.max(1, Math.abs(scaledValue)) * 4;

    return Math.abs(scaledValue - Math.round(scaledValue)) <= tolerance;
};

const resolvePrecision = (cantDecimal?: number | null): number => {
    if (
        !Number.isInteger(cantDecimal) ||
        cantDecimal == null ||
        cantDecimal < DEFAULT_QUANTITY_DECIMALS ||
        cantDecimal > MAX_QUANTITY_DECIMALS
    ) {
        return DEFAULT_QUANTITY_DECIMALS;
    }

    return cantDecimal;
};

const getInputStep = (cantDecimal?: number | null): number =>
    1 / getFactor(resolvePrecision(cantDecimal));

const getMinimum = ({
    cantDecimal,
    storefrontMinimum,
}: Pick<ProductQuantityRules, "cantDecimal" | "storefrontMinimum">): number => {
    const minimumByPrecision = getInputStep(cantDecimal);

    if (storefrontMinimum != null && Number.isFinite(storefrontMinimum) && storefrontMinimum > 1) {
        return Math.max(minimumByPrecision, storefrontMinimum);
    }

    return minimumByPrecision;
};

const getMaximumSelectableStock = (
    stock: number | null | undefined,
    cantDecimal?: number | null,
): number | null => {
    if (stock == null || !Number.isFinite(stock)) {
        return null;
    }

    if (stock <= 0) {
        return 0;
    }

    const factor = getFactor(resolvePrecision(cantDecimal));
    const scaledStock = stock * factor;
    const tolerance = Number.EPSILON * Math.max(1, Math.abs(scaledStock)) * 4;

    return Math.floor(scaledStock + tolerance) / factor;
};

const getInitialQuantity = (rules: ProductQuantityRules): number => {
    const minimum = getMinimum(rules);
    const preferredInitialQuantity = Math.max(1, minimum);
    const maximumStock = getMaximumSelectableStock(rules.stock, rules.cantDecimal);

    if (maximumStock == null) {
        return preferredInitialQuantity;
    }

    if (maximumStock < minimum) {
        return minimum;
    }

    return Math.min(preferredInitialQuantity, maximumStock);
};

const resolveStock = ({
    canonicalStock,
    legacyStock,
    unitMeasureCode,
    allowWithoutStock,
}: ProductQuantityStockRules): ProductQuantityStockResolution => {
    const hasCanonicalStock = canonicalStock != null && Number.isFinite(canonicalStock);
    const missingCanonicalStock = !hasCanonicalStock;

    if (allowWithoutStock) {
        return {
            stock: null,
            missingCanonicalStock,
            usedLegacyStock: false,
        };
    }

    if (hasCanonicalStock) {
        return {
            stock: canonicalStock,
            missingCanonicalStock: false,
            usedLegacyStock: false,
        };
    }

    const effectiveUnitMeasureCode = unitMeasureCode ?? UNIT_MEASURE_CODE;
    const canUseLegacyStock =
        effectiveUnitMeasureCode === UNIT_MEASURE_CODE &&
        legacyStock != null &&
        Number.isFinite(legacyStock);

    return {
        stock: canUseLegacyStock ? legacyStock : 0,
        missingCanonicalStock: true,
        usedLegacyStock: canUseLegacyStock,
    };
};

const normalize = (value: number, cantDecimal?: number | null): number => {
    if (!Number.isFinite(value)) {
        return value;
    }

    const factor = getFactor(resolvePrecision(cantDecimal));
    return Math.round(value * factor) / factor;
};

const parseInput = (rawValue: string, cantDecimal?: number | null): number | null => {
    const value = rawValue.trim();
    const precision = resolvePrecision(cantDecimal);
    const pattern = precision === 0 ? /^\d+$/ : new RegExp(`^\\d+(?:[.,]\\d{1,${precision}})?$`);

    if (!pattern.test(value)) {
        return null;
    }

    const parsedValue = Number(value.replace(",", "."));
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const validate = (value: number, rules: ProductQuantityRules): ProductQuantityValidationResult => {
    const precision = resolvePrecision(rules.cantDecimal);

    if (!Number.isFinite(value)) {
        return { valid: false, error: "not-finite" };
    }

    if (value <= 0) {
        return { valid: false, error: "not-positive" };
    }

    if (!hasValidPrecision(value, precision)) {
        return { valid: false, error: "invalid-precision" };
    }

    if (value < getMinimum(rules)) {
        return { valid: false, error: "below-minimum" };
    }

    const maximumStock = getMaximumSelectableStock(rules.stock, precision);
    if (maximumStock != null && value > maximumStock) {
        return { valid: false, error: "above-stock" };
    }

    return { valid: true };
};

const format = (value: number, cantDecimal?: number | null, locale = "es-AR"): string => {
    if (!Number.isFinite(value)) {
        return "";
    }

    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: resolvePrecision(cantDecimal),
    }).format(value);
};

const increment = (value: number, rules: ProductQuantityRules): number => {
    const precision = resolvePrecision(rules.cantDecimal);
    const minimum = getMinimum(rules);
    const candidate = Math.max(normalize(value + QUANTITY_BUTTON_STEP, precision), minimum);
    const maximumStock = getMaximumSelectableStock(rules.stock, precision);

    if (maximumStock == null) {
        return candidate;
    }

    return Math.min(candidate, maximumStock);
};

const decrement = (value: number, rules: ProductQuantityRules): number => {
    const precision = resolvePrecision(rules.cantDecimal);
    const minimum = getMinimum(rules);
    const candidate = normalize(value - QUANTITY_BUTTON_STEP, precision);

    return Math.max(candidate, minimum);
};

export const productQuantity = {
    resolvePrecision,
    getInputStep,
    getMinimum,
    getMaximumSelectableStock,
    getInitialQuantity,
    resolveStock,
    normalize,
    parseInput,
    validate,
    format,
    increment,
    decrement,
};
